<template>
  <vp-modal class="graph-description-modal" :styles="{ bodyPadding: '16px 36px 36px', buttonsPadding: '0' }">
    <template #header>{{ $t('graphDescription') }}</template>
    <p v-if="textDescription" class="graph-description-modal__description"> {{ textDescription }}</p>
    <h3>{{ chartOptions.title || 'Graph' }}</h3>
    <vp-table
      class="graph-description-modal__info"
      :columns="columns"
      :row-data="rows"
      :show-filters="0"
      :show-search="0"
      :items-per-page="5"
      :sticky-header="true"
      :showCsvExport="1"
    >
      <template #cell="{ column, row }">
        <div v-if="column.id === 'time'" class="graph-description-modal__info-time">
          <span style="display: none;" aria-hidden="true">
          {{ new Date(row.time).toISOString() }}
          </span>
          <span>{{ formatDate(row.time, 'l LT') }}</span>
        </div>
      </template>
    </vp-table>
  </vp-modal>
</template>

<script>
import moment from 'moment';
import VpModal from '../../vp-modal/VpModal.vue';
import VpWidget from '../../vp-widget/VpWidget.vue';
import VpTable from '../../vp-table/VpTable.vue';
import filters from '../../../utilities/common/filters';

export default {
  name: 'VpChartDescriptionModal',
  components: {
    VpModal,
    VpWidget,
    VpTable,
  },
  props: {
    chartData: {
      type: Object,
      default() {
        return {}
      },
    },
    chartOptions: {
      type: Object,
      default() {
        return {}
      },
    },
  },
  computed: {
    deviceKinds() {
      const { datasets = [] } = this.chartData;
      return this.getDataFromDatasets(datasets, 'deviceKind');
    },
    dataUnits() {
      const { datasets = [] } = this.chartData;
      return this.getDataFromDatasets(datasets, 'unit');
    },
    graphResources() {
      const { datasets = [] } = this.chartData;
      return this.getDataFromDatasets(datasets, 'label');
    },
    graphPurpose() {
      const { graphDescription = {} } = this.chartOptions;
      switch (graphDescription.purpose) {
        case 'capacity':
          return this.$i18n.t('theCapacityOfYour');
        case 'energy':
          return this.$i18n.t('theEnergyConsumptionOfYour');
        case 'mode':
          return this.$i18n.t('theModeSetOnYour');
        case 'power':
        case 'evChargerHistory':
          return this.$i18n.t('thePowerConsumedByYour');
        case 'temperature':
          return this.$i18n.t('theTemperatureAtYour');
        default:
          return null;
      }
    },
    textDescription() {
      const { start, end, graphDescription = {} } = this.chartOptions;
      
      // use given description if present
      if (graphDescription.description) return graphDescription.description;
      // skip description when there is no purpose or deviceKind
      if (!this.graphPurpose || !this.deviceKinds) return null;

      let text = this.$i18n.t('thisGraphShows');
      text += ` ${ this.graphPurpose } ${this.deviceKinds}`;
      if (this.dataUnits) {
        text += ` ${this.$i18n.t('asMeasuredIn') } ${this.dataUnits}`;
      }
      if (start) {
        text += ` ${this.$i18n.t('from')} ${moment(start).format('LLLL')}`;
      }
      if (end) {
        text += ` ${this.$i18n.t('toATime')} ${moment(end).format('LLLL')}`;
      }
      if (this.graphResources) {
        text += `. ${this.$i18n.t('itPlots')} ${this.graphResources} ${this.$i18n.t('overTime')}`;
      }

      return text;
    },
    columns() {
      const { datasets = null } = this.chartData;
       if (!Array.isArray(datasets)) return [];

      return datasets.reduce((columns, item = {}) => {
        const { label: label = null, unit } = item;
        if (!label) return columns;
        
        return columns.concat([{ id: label, label: unit ? `${label} (${unit})` : label,  align: 'center' }]);
      }, [{ id: 'time', label: this.$i18n.t('time'), align: 'left' }]);
    },
    rows() {
      const { datasets = null } = this.chartData;
      if (!Array.isArray(datasets)) return [];

      const colIds = this.columns.map(({ id }) => id);
      
      return datasets.reduce((list, item) => {
        const { label, data = [] } = item;
        if (!label) return list;

        for (let i = 0; i < data.length; i += 1) {
          const { x, y } = data[i] || {};
          const targetIndex = list.findIndex((rowItem) => new Date(rowItem.time).getTime() === new Date(x).getTime());
          if (targetIndex > -1) {
            // Add new values to existing row object
            list[targetIndex].time = x;
            list[targetIndex][label] = y;
          } else {
            // Create new row object
            list.push({
              time: x,
              [label]: y,
            });
          }
        }

        return list;
      }, [])
      .sort((a, b) => new Date(b.time).getTime() > new Date(a.time).getTime())
      .map((item, index, list) => {
        const row = item;
        colIds.forEach((colId) => {
          if (list[index][colId] === undefined) {
            const prevValue = (list[index - 1] || {})[colId];
            row[colId] = prevValue !== undefined ? prevValue : 0;
          }
        });
        return row;
      });
    },
  },
  methods: {
    formatDate(date, format) {
      return filters.date(date, format)
    },
    getDataFromDatasets(datasets, property) {
      const values = datasets
        .reduce((list, item) => item[property] && !list.includes(item[property]) ? list.concat(item[property]): list, [])
        .map((item) => this.$i18n.t(item));

      if (!values.length) return null;

      const last = values.pop();
      return values.length > 0 ? `${values.join(', ')} ${this.$i18n.t('and')} ${last}` : last;
    },
  },
}
</script>

<style lang="scss">
.graph-description-modal {
  &__info {
    max-height: 40vh;
    overflow-y: auto;
  }
  &__description {
    height: max-content;
    padding: 5px 0 24px;
  }
}
</style>